import React from 'react';
import radium from 'radium';
import { ThemeContext } from '../theme/Theme.jsx'
import './FormElements.scss';
import Select, { ActionMeta, SingleValue } from 'react-select'

interface LeoSelectProps {
	name: string,
	id: string,
	labeledById?: string,
	value?: any,
	onChange?: any,
	disabled?: boolean,
	error?: boolean,
	children?: React.ReactNode,
	placeholder?: string,
	valueInput?: string,
	options?: { value: any, label: string, isSelected?: boolean, isDisabled?: boolean }[],
	errors?: {error: any, message: string}[],
	className?: string,
	centerText?: boolean,
	smallDropdown? : boolean,
	
}

const defaultProps = {
	disabled: false,
	placeholder: 'Bitte auswählen',
	value: '',
};


function LeoSelect(propsIn: LeoSelectProps) {
	const props = {...defaultProps, ...propsIn};
	const context = React.useContext(ThemeContext);
	
	const style: any = {
		select: {
			background: context.themeManager.value('form-select', 'borderColor', 'bg-color-form-element'),
			borderColor: context.themeManager.value(
				'form-select', 'borderColor', 'border-color-form-element'),
			color: context.themeManager.value('form-select', 'color', 'fg-color-form-element'),
			':focus': {
				borderColor: context.themeManager.value(
					'form-select', 'borderColor', 'border-color-form-element-focus'),
			},
		},
		control: {
			background: context.themeManager.value('form-select', 'borderColor', 'bg-color-form-element'),
			borderColor: context.themeManager.value(
				'form-select', 'borderColor', 'border-color-form-element'),
		},
		input: {
			color: context.themeManager.value('form-select', 'color', 'fg-color-form-element'),
		},
		option: {
			selected: {
				backgroundColor: context.themeManager.valueOrDefault('form-select', 'option-selected-bg-color', null, 'rgba(0,0,0,.08)'),
				color: context.themeManager.value('form-select', 'option-selected-bg-color', 'fg-color-form-element'),
			},
			focused: {
				backgroundColor: context.themeManager.valueOrDefault('form-select', 'option-focused-bg-color', null, 'rgba(0,0,0,.05)'),
			}
		},
	};
	
	const errorMsgStyle = {
		color: context.themeManager.valueOrDefault('form-element-errorMsg', 'color', 'color-error', '#e10101'),
		fontWeight: context.themeManager.valueOrDefault('form-element-errorMsg', 'fontWeight', null, '600'),
		padding: '.3em 0 0 0'
	}
	
	const errStyle: any = {
		control: {
			background: context.themeManager.value('form-select', 'borderColor', 'bg-color-form-element'),
			borderColor:  context.themeManager.valueOrDefault('form-element-errorMsg', 'borderColor', 'color-error', '#e10101'),
			color: context.themeManager.value('form-select', 'color', 'fg-color-form-element'),
		},
		input: {
			color: context.themeManager.value('form-select', 'color', 'fg-color-form-element'),
		},
	};
	
	const handleNativeChange = ((newValue: SingleValue<{   value: any;   label: string; }>, actionMeta: ActionMeta<{   value: any;   label: string; }>) => {
		const event: any = {
			target : {
				name: actionMeta.name,
				value: newValue?.value,
			}
		}
		props.onChange(event)
		// you can do whatever with the event here.
		// event.target.options is a good one :wink:
		// in my case I give it back to `this.props.onChange(event)`
	})
	
	const targetHeight = props.smallDropdown? 25 : 40;
	
	return (
		<div className="leo-select__container">
			<Select
				styles={{
					control: (baseStyles, state) => ({
						...baseStyles,
						borderColor: props.errors ? errStyle.control.borderColor : style.control.borderColor,
						background: props.errors ? errStyle.control.background : style.control.background,
						textAlign: 'center',
						borderRadius: 0,
						boxShadow: 'none',
						outline: state.isFocused ? 'auto' : 'none',
						outlineColor: props.errors ? errStyle.control.borderColor : style.control.outlineColor,
						paddingLeft: !props.centerText ? "2.8em" : "1.5em",
						':hover': {
							borderColor: props.errors ? errStyle.control.borderColor : style.control.borderColor,
						},
						minHeight: 'initial',
					}),
					valueContainer: (base: any) => ({
						...base,
						height: `${targetHeight - 1 - 1}px`,
						padding: '0 8px',
					}),
					clearIndicator: (base: any) => ({
						...base,
						padding: `${(targetHeight - 20 - 1 - 1) / 2}px`,
					}),
					dropdownIndicator: (base: any) => ({
						...base,
						padding: `${(targetHeight - 20 - 1 - 1) / 2}px`,
					}),
					input: (baseStyles) => ({
						...baseStyles,
						color: props.errors ? errStyle.input.color : style.input.color,
					}),
					placeholder: (baseStyles) => ({
						...baseStyles,
						color: '#777575',
						fontStyle: 'italic',
					}),
					menu: (baseStyles) => ({
						...baseStyles,
						marginTop: '0',
						borderRadius: '0',
					}),
					menuList:(baseStyles) => ({
						...baseStyles,
						paddingTop: '0',
					}),
					option: (baseStyles,state) => ({
						...baseStyles,
						backgroundColor: state.isSelected || (state.isSelected && state.isFocused)?
							style.option.selected.backgroundColor: state.isFocused ?
								style.option.focused.backgroundColor :
								style.input.backgroundColor,
						color: state.isSelected? style.option.selected.color : style.input.color,
						':active': {
							...baseStyles[':active'],
							backgroundColor: style.option.selected.backgroundColor,
						}
					})
				}}
				placeholder={props.placeholder}
				inputId={props.id}
				name={props.name}
				isDisabled={props.disabled}
				options={props.options}
				{...(props.labeledById ? {'aria-labelledby': props.labeledById} : {})}
				aria-invalid={!!props.errors}
				aria-errormessage={"error_" + props.id}
				isSearchable={false}
				onChange={handleNativeChange}
				value={props.options?.filter(option => option.value == props.value)}
				className={props?.className}
			>
			</Select>
			{props.errors &&
				<div id={"error_" + props.id} style={errorMsgStyle}>{
					props.errors.map((error) => error.message)}
				</div>
			}
		</div>
	);
}

export default radium(LeoSelect);
